/**
 * Leads, contacts, consent and the conversation around them (03 §4.2). Contact details are PII:
 * never logged, never in a URL; guests only ever see their own, masked.
 */
import { z } from 'zod';
import { CostCents } from './agent.js';
import { Actor, Email, Id, Instant, PhoneE164, Timestamps } from './common.js';
import { Plan } from './quote.js';
/**
 * Derived by `leads/stage.ts` (03 §4.5) and stored for querying; recomputed on every relevant write.
 * Order is the funnel order.
 */
export const Stage = z.enum([
    'new',
    'engaged',
    'qualified',
    'tentative',
    'proposed',
    'booked',
    'balance_due',
    'completed',
    'lost',
]);
/** How a stage reads to people (timeline lines, refusals, the concierge's context). */
export const STAGE_NAMES = {
    new: 'New',
    engaged: 'Engaged',
    qualified: 'Qualified',
    tentative: 'Tentative',
    proposed: 'Proposed',
    booked: 'Booked',
    balance_due: 'Balance due',
    completed: 'Completed',
    lost: 'Lost',
};
/**
 * The stages staff may set by hand (the 2026-10-28 demo: much of the process happens on the phone and by
 * email). Booked, Balance due and Completed come from money and the event, never from a hand.
 */
export const ManualStage = Stage.exclude(['booked', 'balance_due', 'completed']);
/** Stages from payments and the event: a hand can neither set them nor move a lead off them. */
const MONEY_STAGES = ['booked', 'balance_due', 'completed'];
/** The only stages a hand may set below what the lead's records say (they rest on conversation, not rows). */
export const SOFT_STAGES = ['new', 'engaged', 'qualified'];
const stageRank = (s) => Stage.options.indexOf(s);
/**
 * Why staff may not set `target` on a lead whose records say `derived` (the stage without any hand-set
 * one, after a lost lead is reopened), or null when they may. Lost also needs a reason (the API checks).
 * The API and the console's picker and board share this rule.
 */
export function manualStageRefusal(target, derived) {
    if (MONEY_STAGES.includes(target))
        return `${STAGE_NAMES[target]} comes from payments and the event; it cannot be set by hand.`;
    if (MONEY_STAGES.includes(derived))
        return `This lead is ${STAGE_NAMES[derived]}; its stage follows the booking and its payments.`;
    if (target === 'lost' || stageRank(target) >= stageRank(derived))
        return null;
    if (SOFT_STAGES.includes(target))
        return null;
    return `The lead's records say ${STAGE_NAMES[derived]}; only New, Engaged or Qualified can be set below that.`;
}
/** The stages staff may pick for a lead whose records say `derived` (funnel order, Lost last). */
export function manualStageOptions(derived) {
    return ManualStage.options.filter((s) => manualStageRefusal(s, derived) === null);
}
export const LeadSource = z.enum([
    'website',
    'standalone',
    'link',
    'qr',
    'instagram',
    'referral',
    'phone',
    'staff',
    'email',
    'sms',
    'other',
    /** A staff sandbox lead from Try it (phase 1); deleted with its session. */
    'try_it',
    /** Imported from Perfect Venue's CSV export (phase 3, 05 §9); never a guest-supplied source. */
    'perfect_venue',
]);
// ── Contact and consent ───────────────────────────────────────────────────
const contactFields = {
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().max(80).nullable(),
    email: Email,
    phone: PhoneE164.nullable(),
    isPlanner: z.boolean(),
    company: z.string().max(120).nullable(),
};
export const Contact = z.object({
    id: Id,
    ...contactFields,
    notes: z.string().max(4000).nullable(),
    ...Timestamps,
});
export const ContactInput = z.object({ ...contactFields, notes: z.string().max(4000).nullable().optional() });
/** What a guest sees of their own contact: `m***@gmail.com`, `•••• 5553`. */
export const MaskedContact = z.object({
    firstName: z.string(),
    lastName: z.string().nullable(),
    emailMasked: z.string(),
    phoneMasked: z.string().nullable(),
    isPlanner: z.boolean(),
});
export const ConsentChannel = z.enum(['sms', 'email']);
/**
 * How the guest prefers to be contacted (the plan scene's Text / Email choice; staff may change it).
 * The concierge uses it for a reply or a follow-up when the send policy allows that channel (SMS needs
 * consent and a mobile); otherwise the usual choice stands. Null: no preference given.
 */
export const PreferredChannel = z.enum(['sms', 'email']);
/**
 * `implied` (phase 2, 11 §3.3): no written consent, but the guest texted or called this number first;
 * covers a customer-care SMS reply only (their last inbound within the window, no outbound SMS since).
 */
export const ConsentStatus = z.enum(['granted', 'denied', 'revoked', 'implied']);
/** Where a consent row came from (phase 2). Phase-0 rows hold the free text `guest plan`. */
export const ConsentSource = z.enum(['plan_scene', 'inbound_sms', 'inbound_call', 'staff', 'import']);
/**
 * What revoked a channel (phase 2): STOP, the email unsubscribe link, a bounce, staff, a spam complaint;
 * `carrier_opt_out`: the venue's SMS provider (Dialpad's company opt-out list) says the number opted out.
 */
export const ConsentRevokedBy = z.enum([
    'stop_keyword',
    'unsubscribe_link',
    'bounce',
    'staff',
    'complaint',
    'carrier_opt_out',
]);
export const Consent = z.object({
    id: Id,
    contactId: Id,
    channel: ConsentChannel,
    status: ConsentStatus,
    capturedAt: Instant,
    /** A `ConsentSource` value from phase 2 on; phase-0 rows say `guest plan`. */
    source: z.string(),
    /** The exact text the guest saw (the brief's `textShown`; `POST /g/consent` stores it here). */
    evidence: z.string(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    revokedAt: Instant.nullable(),
    /** Set with `revokedAt` (phase 2). */
    revokedBy: ConsentRevokedBy.nullish(),
    ...Timestamps,
});
// ── Lead ──────────────────────────────────────────────────────────────────
export const PriorityKey = z.enum(['budget', 'date', 'view', 'privacy', 'kosher', 'kids', 'other']);
/** What the guest said matters, with the message that showed it. */
export const Priority = z.object({
    key: PriorityKey,
    note: z.string().max(500),
    messageId: Id.nullable(),
});
/** The staff view of a lead. The magic-token hash is never on the wire. */
export const Lead = z.object({
    id: Id,
    contactId: Id.nullable(),
    source: LeadSource,
    stage: Stage,
    /**
     * The stage a Perfect Venue import gave the lead (phase 3): a floor for the derived stage until a hold,
     * quote, agreement, booking or close supersedes it, then null. Absent from older APIs.
     */
    importedStage: Stage.nullish(),
    plan: Plan,
    priorities: z.array(Priority),
    competingVenues: z.array(z.string()),
    qualifiedAt: Instant.nullable(),
    lostReason: z.string().nullable(),
    lostAt: Instant.nullable(),
    nextReviewAt: Instant.nullable(),
    reviewReason: z.string().nullable(),
    ownerStaffId: Id.nullable(),
    pausedUntil: Instant.nullable(),
    /**
     * Per-lead pause (phase 2, 11 §4.3): while set, no trigger wakes the case except `handed_back` and
     * unpause. Absent from a phase-1 API.
     */
    pausedAt: Instant.nullish(),
    pausedBy: Actor.nullish(),
    responseFirstAt: Instant.nullable(),
    lastInboundAt: Instant.nullable(),
    lastOutboundAt: Instant.nullable(),
    touchesUnanswered: z.number().int(),
    /** How the guest prefers to be contacted. Absent from an API before this field. */
    preferredChannel: PreferredChannel.nullish(),
    /**
     * The stage staff set by hand (`PATCH /s/leads/:id {stage}`), or null when code derives it. A floor:
     * the lead never shows below it, and a later real signal (a hold, quote, agreement, booking, payment,
     * close or qualification) that derives further moves it on and clears it. New, Engaged and Qualified
     * may sit below the derived stage until such a signal. Absent from an API before this field.
     */
    manualStage: ManualStage.nullish(),
    /** `staff:<uuid>` who set `manualStage`. */
    manualStageSetBy: z.string().nullish(),
    manualStageSetAt: Instant.nullish(),
    /**
     * The staff member who put the plan together for the guest (`PATCH /s/leads/:id/plan`), and when.
     * Cleared when the guest changes the plan themselves. Absent from an API before this field.
     */
    planSetBy: Id.nullish(),
    planSetAt: Instant.nullish(),
    ...Timestamps,
});
// ── Qualified (phase 4, 13 §2; the tours badge is `LeadToursBadge` in tours.ts) ──
/**
 * The conditions of 03 §4.5, each as code last found it: `qualifiedAt` is set when all hold and cleared
 * when one stops holding. For the console's Qualified badge and its "why".
 */
export const Qualification = z.object({
    qualifiedAt: Instant.nullable(),
    /** A quote for the lead's headcount exists and was shown or sent. */
    quoteShown: z.boolean(),
    /** The lead's date is open, or the lead is flexible on it. */
    dateOpenOrFlexible: z.boolean(),
    /** The minimums article, or the quote's min-spend line, was shown. */
    minimumsShown: z.boolean(),
    /** No `ask_staff` question is pending. */
    noPendingQuestion: z.boolean(),
});
/** The guest's view of their own lead. */
export const GuestLead = z.object({
    id: Id,
    source: LeadSource,
    stage: Stage,
    plan: Plan,
    /** How the guest prefers to be contacted. Absent from an API before this field. */
    preferredChannel: PreferredChannel.nullish(),
    /**
     * Set while the plan is the one staff put together for the guest: the line Your plan shows above the
     * summary ("Put together for you by Miriam at Becca"). Null once the guest changes the plan.
     */
    planBy: z.string().nullish(),
    createdAt: Instant,
    updatedAt: Instant,
});
// ── Messages, notes, questions, calls ─────────────────────────────────────
/** `system` carries timeline events (hold expired, stage changed) — not sent anywhere. */
export const MessageChannel = z.enum(['web', 'email', 'sms', 'staff_sms', 'push', 'system']);
export const MessageDirection = z.enum(['inbound', 'outbound', 'internal']);
/**
 * Phase 2 adds the autonomy states of an outbound message: `drafted` (written at `observe`, never sent),
 * `awaiting_approval` (parked at `approve`), `rejected` and `expired` (the approval's outcome). `queued`
 * with `scheduledAt` is a touch held for the end of quiet hours.
 */
export const MessageStatus = z.enum([
    'queued',
    'sent',
    'delivered',
    'failed',
    'bounced',
    'received',
    'logged',
    'drafted',
    'awaiting_approval',
    'rejected',
    'expired',
]);
/**
 * What a message is for (phase 2):
 * - `reply` — answers the guest's last inbound (customer care; `send_plan_link` is one);
 * - `touch` — a proactive follow-up, bound by the ceilings and the something-new and personal rules;
 * - `bounce` — an inbound bounce or spam complaint (wakes the case, revokes the channel);
 * - `auto_reply` — an inbound out-of-office (stored, never wakes the case);
 * - `keyword_reply` — code's answer to STOP or HELP (never the model);
 * - `notification` — to staff (`notify_staff`, approval prompts, verification codes).
 * Null on inbound guest messages and phase-1 rows.
 */
export const MessageKind = z.enum([
    'reply',
    'touch',
    'bounce',
    'auto_reply',
    'keyword_reply',
    'notification',
]);
export const Message = z.object({
    id: Id,
    leadId: Id.nullable(),
    channel: MessageChannel,
    direction: MessageDirection,
    author: Actor,
    /** Template key when the message was rendered from one. */
    templateKey: z.string().nullable(),
    subject: z.string().nullable(),
    body: z.string(),
    bodyHtml: z.string().nullable(),
    status: MessageStatus,
    providerMessageId: z.string().nullable(),
    inReplyTo: z.string().nullable(),
    /** Knowledge/tool facts the text cites (phase 1 grounding). */
    factIds: z.array(z.string()),
    // Phase 2 (11 §3.5); absent from a phase-1 API.
    kind: MessageKind.nullish(),
    /** When a `queued` touch goes out (end of quiet hours). */
    scheduledAt: Instant.nullish(),
    /** SMS segments billed. */
    segments: z.number().int().min(0).nullish(),
    /** Provider cost, fractional cents (Twilio prices per segment below a cent). */
    costCents: CostCents.nullish(),
    /** The provider's failure or bounce description. Never contains the body. */
    error: z.string().max(2000).nullish(),
    ...Timestamps,
});
export const StaffNoteKind = z.enum(['note', 'instruction']);
export const StaffNote = z.object({
    id: Id,
    leadId: Id,
    authorStaffId: Id,
    kind: StaffNoteKind,
    body: z.string(),
    ...Timestamps,
});
export const QuestionStatus = z.enum(['open', 'answered', 'dismissed']);
export const QuestionUrgency = z.enum(['low', 'normal', 'high']);
/** A question for the events team: the guest's "ask us" form now, the concierge's `ask_staff` later. */
export const StaffQuestion = z.object({
    id: Id,
    leadId: Id,
    askedBy: Actor,
    question: z.string(),
    urgency: QuestionUrgency,
    status: QuestionStatus,
    answer: z.string().nullable(),
    answeredByStaffId: Id.nullable(),
    answeredAt: Instant.nullable(),
    ...Timestamps,
});
/** `missed`, `voicemail` and `busy` start the missed-call path (11 §3.3). */
export const CallStatus = z.enum(['missed', 'voicemail', 'answered', 'transferred', 'busy']);
export const CallSource = z.enum(['hostie_transfer', 'direct']);
export const CallDirection = z.enum(['inbound', 'outbound']);
/**
 * A phone call (phase 2 shape, 11 §2.1). The brief's `from` is `caller`. Carries `venueId` because the
 * calls module and the review job pass it between them; no request body ever names a venue.
 */
export const Call = z.object({
    id: Id,
    venueId: Id,
    leadId: Id.nullable(),
    direction: CallDirection,
    caller: PhoneE164,
    to: PhoneE164,
    startedAt: Instant,
    status: CallStatus,
    source: CallSource,
    durationSec: z.number().int().min(0).nullable(),
    /** Always null in phase 2: recordings are not stored. */
    recordingUrl: z.url().nullable(),
    /** Twilio `CallSid`; the webhooks are idempotent on it. */
    providerCallId: z.string().max(64).nullable(),
    ...Timestamps,
});
// ── Timeline ──────────────────────────────────────────────────────────────
export const TimelineKind = z.enum([
    'message',
    'note',
    'hold',
    'quote',
    'question',
    'call',
    'stage',
    'contact',
]);
/** One row of a lead's timeline, newest last; `refId` points at the row of that kind. */
export const TimelineEntry = z.object({
    at: Instant,
    kind: TimelineKind,
    refId: Id.nullable(),
    actor: Actor,
    summary: z.string(),
});
//# sourceMappingURL=leads.js.map